import { makeAutoObservable } from 'mobx';
import { Label, Milestone, RawProjectTemplate } from '../interfaces/project';
import { StatusListInterface } from '../../interfaces/ProjectInterface';
import { createObservableTask, TaskModel } from './task';
import { rootStore } from '../rootStore';
import { TaskGroupInterface } from '../../interfaces/TaskInterface';
import { formatTemplateTasks } from '../../services/mobxTaskFilterService';
import { RawTask } from '../interfaces/task';
import { genericUpsert } from '../common';

export class ProjectTemplateModel {
  id: string;
  name: string;
  description: string;
  createdBy: string;
  createdTime: Date;
  updatedBy: string;
  updatedTime: Date | null;
  updated: boolean = false;
  isArchived: boolean = false;
  value: string;
  companyId: string;

  isEstimate: boolean = false;
  isMilestone: boolean = false;
  multiAssignee?: boolean = false;
  priority: {
    enabled: boolean;
    default: number;
  };
  statusData: StatusListInterface[];
  statusEnable: boolean = false;
  labelsEnabled: boolean = false;
  labelsList: Label[] = [];
  isGroupEnabled: boolean = false;
  taskGroups: TaskGroupInterface[] = [];
  milestones: Milestone[] = [];

  tasks: TaskModel[] = [];
  userIds: string[] = [];

  constructor(rawProjectTemplate: RawProjectTemplate) {
    makeAutoObservable(this);

    this.id = rawProjectTemplate.id;
    this.name = rawProjectTemplate.name;
    this.description = rawProjectTemplate.description;
    this.createdBy = rawProjectTemplate.createdBy;
    this.createdTime = new Date(rawProjectTemplate.createdTime);
    this.updatedBy = rawProjectTemplate.updatedBy;
    this.companyId = rawProjectTemplate.companyId;
    this.updatedTime = rawProjectTemplate.updatedTime ? new Date(rawProjectTemplate.updatedTime) : null;
    this.updated = rawProjectTemplate.updated ?? false;
    this.isArchived = rawProjectTemplate.isArchived;
    this.value = rawProjectTemplate.value;
    this.isEstimate = rawProjectTemplate.isEstimate;
    this.isMilestone = rawProjectTemplate.isMilestone;
    this.multiAssignee = rawProjectTemplate.multiAssignee;
    this.priority = rawProjectTemplate.priority;
    this.statusData = rawProjectTemplate.statusData;
    this.statusEnable = rawProjectTemplate.statusEnable;
    this.labelsEnabled = rawProjectTemplate.labelsEnabled;
    this.labelsList = rawProjectTemplate.labelsList;
    this.multiAssignee = rawProjectTemplate.multiAssignee;
    this.isGroupEnabled = rawProjectTemplate.isGroupEnabled;
    this.taskGroups = rawProjectTemplate.groups;
    this.tasks = rawProjectTemplate.tasks.map(createObservableTask);
    this.userIds = rawProjectTemplate.userIds;
    this.milestones = [];
  }

  get defaultStatus() {
    return this.statusData.find((status) => status.Default);
  }

  get users() {
    return rootStore.workspaceStore.currentWorkspace?.users.filter((user) => this.userIds.includes(user.id)) || [];
  }

  get filteredProjectTemplateTasks() {
    return formatTemplateTasks(this.tasks, this);
  }

  setUsersIds(userIds: string[]) {
    this.userIds = userIds;
    this.updated = true;
  }

  setGroupEnabled(isGroupEnabled: boolean) {
    this.isGroupEnabled = isGroupEnabled;
    this.updated = true;
  }

  setPriorityEnabled(priorityEnabled: boolean) {
    this.priority.enabled = priorityEnabled;
    this.updated = true;
  }

  setDefaultPriority(defaultPriority: number) {
    this.priority.default = defaultPriority;
    this.updated = true;
  }

  setStatusEnabled(statusEnabled: boolean) {
    this.statusEnable = statusEnabled;
    this.updated = true;
  }

  setLabelsEnabled(labelsEnabled: boolean) {
    this.labelsEnabled = labelsEnabled;
    this.updated = true;
  }

  setLabelsList(labelsList: Label[]) {
    this.labelsList = labelsList;
    this.updated = true;
  }

  setStatusData(statusData: StatusListInterface[]) {
    this.statusData = statusData;
    this.updated = true;
  }

  setTaskGroups(groups: TaskGroupInterface[]) {
    this.taskGroups = groups;
    this.updated = true;
  }

  setMultiAssignee(multiAssignee: boolean) {
    this.multiAssignee = multiAssignee;
    this.updated = true;
  }

  setEstimate(isEstimate: boolean) {
    this.isEstimate = isEstimate;
    this.updated = true;
  }

  setMilestone(isMilestone: boolean) {
    this.isMilestone = isMilestone;
    this.updated = true;
  }

  setUpdated(updated: boolean) {
    this.updated = updated;
  }

  setTasks(tasks: TaskModel[]) {
    this.tasks = tasks;
    this.updated = true;
  }

  addTask(task: RawTask) {
    this.tasks.push(createObservableTask(task));
    this.updated = true;
  }

  upsertTasks(rawTasks: RawTask[]) {
    this.updated = true;
    const { newItems, updatedItems } = genericUpsert(this.tasks, rawTasks, createObservableTask);
    return { newItems, updatedItems };
  }
}

export const createProjectTemplateModel = (rawProjectTemplate: RawProjectTemplate) => {
  return new ProjectTemplateModel(rawProjectTemplate);
};
